'use client';

/**
 * INBOX-13a — Settings → Inbox → Telegram connect state.
 *
 * Feature-local and NOT persisted: the `/start` deep link is a bearer link token, so it lives in memory only and goes
 * with the session (`clearWorkspaceSessionKeys` calls `reset()`). Reaching a terminal link state clears it too.
 *
 * `refresh()` reads `InboxTelegramStatusRequest`; `connect()` mints a link and refreshes once on ok; `startPolling()`
 * re-reads every 3 s until a terminal state, 15 min (`pollExpired`), the returned stop function, or `reset()`.
 *
 * A refused read (`error_code` set, no state) is "not known", not a state: the previous status stays, the code goes to
 * `refusal`, and a running poll keeps going. After an ok Disconnect, `disconnected()` shows NOT_LINKED at once and
 * ignores replies that still name the removed source (its purge runs in the background) until the `removed` push
 * (`sourceRemoved()`, from `registerInboxHandlers`), a reply without it, or a DISCONNECT_REMOVED_WAIT_MS re-read.
 */
import { create as createMessage } from '@bufbuild/protobuf';
import { create } from 'zustand';
import {
  InboxErrorCode,
  InboxTelegramConnectRequestSchema,
  InboxTelegramLinkState,
  InboxTelegramStatusRequestSchema,
} from '@/lib/protobuf/gen/inbox/inbox_pb';
import { sendRequest } from '@/lib/websocket';
import {
  DISCONNECT_REMOVED_WAIT_MS,
  TELEGRAM_CONSENT_VERSION,
  TELEGRAM_POLL_INTERVAL_MS,
  TELEGRAM_POLL_MAX_MS,
} from './constants';
import type { ActionResult } from './inboxStore';
import { transportFailure } from './storeResults';

export interface TelegramStatusView {
  state: InboxTelegramLinkState;
  /** TELEGRAM_ALREADY_LINKED when the newest /start was refused; UNSPECIFIED otherwise. */
  errorCode: InboxErrorCode;
  canReply: boolean;
  extraRights: boolean;
  botUsername: string;
  accountRef: string;
}

export type TelegramLoadState = 'idle' | 'loading' | 'ready' | 'error';

export interface TelegramState {
  status: TelegramStatusView | null;
  loadState: TelegramLoadState;
  deepLink: string | null;
  pendingConnect: boolean;
  /** The newest status read was refused in-band (no state); UNSPECIFIED once a read answers with a state. */
  refusal: InboxErrorCode;
  /** A poll stopped at the 15-minute cap (not at a terminal state); cleared by `connect()` and `resumePolling()`. */
  pollExpired: boolean;
  actions: {
    refresh(): Promise<void>;
    connect(): Promise<ActionResult<object>>;
    /** Starts a 3 s status poll; returns its stop function. */
    startPolling(): () => void;
    /** "Check again" after the cap: clears `pollExpired` (the screen re-arms its poll) and reads the status once. */
    resumePolling(): void;
    /** An ok Disconnect of `accountRef`: show NOT_LINKED now, and hold it while the server purge finishes. */
    disconnected(accountRef: string): void;
    /** The `removed` push for a Telegram source. */
    sourceRemoved(accountRef: string): void;
    reset(): void;
  };
}

const TERMINAL: ReadonlySet<InboxTelegramLinkState> = new Set([
  InboxTelegramLinkState.NOT_LINKED,
  InboxTelegramLinkState.NO_ALWAYS_ON_SPACE,
  InboxTelegramLinkState.CONNECTED,
  InboxTelegramLinkState.PAUSED,
  InboxTelegramLinkState.TOKEN_EXPIRED,
]);

export function isTerminalTelegramState(state: InboxTelegramLinkState | undefined): boolean {
  return state !== undefined && TERMINAL.has(state);
}

const initialData = (): Omit<TelegramState, 'actions'> => ({
  status: null,
  loadState: 'idle',
  deepLink: null,
  pendingConnect: false,
  refusal: InboxErrorCode.UNSPECIFIED,
  pollExpired: false,
});

const notLinked = (): TelegramStatusView => ({
  state: InboxTelegramLinkState.NOT_LINKED,
  errorCode: InboxErrorCode.UNSPECIFIED,
  canReply: false,
  extraRights: false,
  botUsername: '',
  accountRef: '',
});

// A reply that lands after reset() (logout) belongs to the previous session and is dropped.
let generation = 0;
const pollers = new Set<() => void>();
/** The source an ok Disconnect removed while its background purge runs; replies naming it are stale. */
let removedRef: string | null = null;
let removedTimer: ReturnType<typeof setTimeout> | null = null;
function clearRemoved() {
  removedRef = null;
  if (removedTimer !== null) clearTimeout(removedTimer);
  removedTimer = null;
}

export const useTelegramStore = create<TelegramState>()((set, get) => ({
  ...initialData(),
  actions: {
    refresh: async () => {
      const gen = generation;
      set({ loadState: 'loading' });
      try {
        const resp = await sendRequest(
          { case: 'inboxTelegramStatusRequest', value: createMessage(InboxTelegramStatusRequestSchema, {}) },
          'inboxTelegramStatusResponse',
        );
        if (gen !== generation) return;
        if (resp.errorCode !== InboxErrorCode.UNSPECIFIED && resp.state === InboxTelegramLinkState.UNSPECIFIED) {
          // A refusal carries no state: keep what is known (R3 I-1).
          set({ loadState: 'error', refusal: resp.errorCode });
          return;
        }
        if (removedRef !== null) {
          if (resp.accountRef === removedRef) {
            set({ loadState: 'ready', refusal: InboxErrorCode.UNSPECIFIED });
            return;
          }
          clearRemoved();
        }
        const status: TelegramStatusView = {
          state: resp.state,
          errorCode: resp.errorCode,
          canReply: resp.canReply,
          extraRights: resp.extraRights,
          botUsername: resp.botUsername,
          accountRef: resp.accountRef,
        };
        set((s) => ({
          status,
          loadState: 'ready',
          refusal: InboxErrorCode.UNSPECIFIED,
          deepLink: TERMINAL.has(status.state) ? null : s.deepLink,
        }));
      } catch {
        if (gen !== generation) return;
        set({ loadState: 'error' });
      }
    },

    connect: async () => {
      const gen = generation;
      set({ pendingConnect: true });
      try {
        const resp = await sendRequest(
          {
            case: 'inboxTelegramConnectRequest',
            value: createMessage(InboxTelegramConnectRequestSchema, { consentVersion: TELEGRAM_CONSENT_VERSION }),
          },
          'inboxTelegramConnectResponse',
        );
        if (resp.errorCode !== InboxErrorCode.UNSPECIFIED) return { ok: false, errorCode: resp.errorCode };
        if (!resp.ok) return { ok: false, errorCode: InboxErrorCode.INTERNAL };
        if (gen !== generation) return { ok: true };
        clearRemoved();
        set({ deepLink: resp.deepLink || null, pollExpired: false });
        void get().actions.refresh();
        return { ok: true };
      } catch {
        return transportFailure;
      } finally {
        if (gen === generation) set({ pendingConnect: false });
      }
    },

    startPolling: () => {
      let ticks = 0;
      const maxTicks = Math.ceil(TELEGRAM_POLL_MAX_MS / TELEGRAM_POLL_INTERVAL_MS);
      let inFlight = false;
      const timer = setInterval(() => {
        ticks++;
        if (isTerminalTelegramState(get().status?.state)) {
          stop();
          return;
        }
        if (ticks > maxTicks) {
          stop();
          set({ pollExpired: true });
          return;
        }
        if (inFlight) return;
        inFlight = true;
        void get()
          .actions.refresh()
          .finally(() => {
            inFlight = false;
          });
      }, TELEGRAM_POLL_INTERVAL_MS);
      const stop = () => {
        clearInterval(timer);
        pollers.delete(stop);
      };
      pollers.add(stop);
      return stop;
    },

    resumePolling: () => {
      set({ pollExpired: false });
      void get().actions.refresh();
    },

    disconnected: (accountRef) => {
      clearRemoved();
      removedRef = accountRef;
      const gen = generation;
      removedTimer = setTimeout(() => {
        removedTimer = null;
        if (gen !== generation) return;
        removedRef = null;
        void get().actions.refresh();
      }, DISCONNECT_REMOVED_WAIT_MS);
      set({ status: notLinked(), deepLink: null, pollExpired: false, refusal: InboxErrorCode.UNSPECIFIED });
    },

    sourceRemoved: (accountRef) => {
      const ours = accountRef === removedRef || accountRef === get().status?.accountRef;
      if (accountRef === removedRef) clearRemoved();
      if (ours) void get().actions.refresh();
    },

    reset: () => {
      generation++;
      clearRemoved();
      for (const stop of [...pollers]) stop();
      set(initialData());
    },
  },
}));
